import {
  Box,
  Button,
  Container,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { memo, useEffect, useState } from "react";
import ChartComponent from "../../../components/ChartComponent";
import RESET_LEFT from "../../../../src/assets/images/arrow-right (6).png";
import RESET_RIGHT from "../../../../src/assets/images/arrow-right (7).png";
import { AFFECTED_EAR } from "../../../utils/constants";

const Audiogram = ({ fields, setFields }) => {
  const [rightBcData, setRightBcData] = useState([]);
  const [leftBcData, setLeftBcData] = useState([]);

  useEffect(() => {
    if (fields?.rf) {
      setRightBcData([
        fields?.rf?.rf250,
        fields?.rf?.rf500,
        fields?.rf?.rf1k,
        fields?.rf?.rf2k,
        fields?.rf?.rf4k,
        fields?.rf?.rf6k,
      ]);
    }
  }, [fields?.rf]);

  useEffect(() => {
    if (fields?.lf) {
      setLeftBcData([
        fields?.lf?.lf250,
        fields?.lf?.lf500,
        fields?.lf?.lf1k,
        fields?.lf?.lf2k,
        fields?.lf?.lf4k,
        fields?.lf?.lf6k,
      ]);
    }
  }, [fields?.lf]);

  const handleResetRightChart = () => {
    setRightBcData(new Array(6).fill(null));

    setFields({
      ...fields,
      err: "",
      rf: {
        rf250: null,
        rf500: null,
        rf1k: null,
        rf2k: null,
        rf4k: null,
        rf6k: null,
      },
    });
  };

  const handleResetLeftChart = () => {
    setLeftBcData(new Array(6).fill(null));
    setFields({
      ...fields,
      err: "",
      lf: {
        lf250: null,
        lf500: null,
        lf1k: null,
        lf2k: null,
        lf4k: null,
        lf6k: null,
      },
    });
  };

  const handleValueChange = (index, value, mode, ear) => {
    const frequencies = ["250", "500", "1k", "2k", "4k", "6k"];
    const frequencyKey = `${ear === "right" ? "rf" : "lf"}${
      frequencies[index]
    }`;

    const newData = {
      ...fields,
      [ear === "right" ? "rf" : "lf"]: {
        ...fields[ear === "right" ? "rf" : "lf"],
        [frequencyKey]: value,
      },
    };

    if (ear === "right") {
      setRightBcData((prev) => {
        const newBcData = [...prev];
        newBcData[index] = value;
        return newBcData;
      });
    } else if (ear === "left") {
      setLeftBcData((prev) => {
        const newBcData = [...prev];
        newBcData[index] = value;
        return newBcData;
      });
    }
    setFields({ ...newData, err: "" });
  };

  return (
    <>
      <Box sx={{ ml: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "center", mb:0.5, mr: { xs: 3, sm: 25, md: 30 } }}>
          <FormLabel
            component="legend"
            sx={{ fontWeight: "bold", color: "#A91674", fontSize: "16px" }}
          >
            Affected Ear*
          </FormLabel>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center"}}>  
        <RadioGroup
          row
          value={fields?.affected_ear}
          onChange={(e) =>
            setFields({
              ...fields,
              err: "",
              affected_ear: Number(e.target.value),
            })
          }
          sx={{
            border: "2px solid #A91674",
            borderRadius: "8px",
            pl: "8px",
            mb: 1,
            wordBreak: "break-word",
            width: { xs: "80%", sm: "50%", md: "30%" },
            justifyContent: "space-evenly",
            flexWrap: "wrap",
          }}
        >
          <FormControlLabel
            value={AFFECTED_EAR.LEFT}
            control={<Radio sx={{ color: "#A91674" }} />}
            label="Left"
          />
          <FormControlLabel
            value={AFFECTED_EAR.RIGHT}
            control={<Radio sx={{ color: "#A91674" }} />}
            label="Right"
          />
          <FormControlLabel
            value={AFFECTED_EAR.BOTH}
            control={<Radio sx={{ color: "#A91674" }} />}
            label="Both"
          />
        </RadioGroup>
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ p: { xs: 2, sm: 4 } }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
            gap: 4,
          }}
        >
          {(fields?.affected_ear === AFFECTED_EAR.RIGHT ||
            fields?.affected_ear === AFFECTED_EAR.BOTH) && (
            <Box
              pb={3}
              sx={{
                backgroundColor: "#FFF6F6",
                width: { xs: "100%", sm: "70%", md: "35%" },
                maxHeight: "63vh",
                border: "1px solid rgb(228, 217, 217)",
                borderRadius: "4px",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "center",
                }}
              >
                <Typography color="error" variant="h5" sx={{ mt:1 }}>
                  • Right
                </Typography>
              </Box>
              
              <Box sx={{ p: 1 }}>
              <ChartComponent
                allowed_bc={true}
                onValueChange={(index, value, mode) =>
                  handleValueChange(index, value, "bc", "right")
                }
                data={rightBcData}
                title="BC"
              />
              </Box>
            </Box>
          )}

          {fields.affected_ear !== undefined && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                height: "48px",
                gap: 2,
              }}
            >
              {(fields?.affected_ear === AFFECTED_EAR.RIGHT ||
                fields?.affected_ear === AFFECTED_EAR.BOTH) && (
                <Button onClick={handleResetRightChart}>
                  <img
                    src={RESET_RIGHT}
                    alt="Reset Right"
                    style={{ height: "58px", maxWidth: "100%" }}
                  />
                </Button>
              )}
              {(fields?.affected_ear === AFFECTED_EAR?.LEFT ||
                fields?.affected_ear === AFFECTED_EAR?.BOTH) && (
                <Button onClick={handleResetLeftChart}>
                  <img
                    src={RESET_LEFT}
                    alt="Reset Left"
                    style={{ height: "58px", maxWidth: "100%" }}
                  />
                </Button>
              )}
            </Box>
          )}

          {(fields?.affected_ear === AFFECTED_EAR?.LEFT ||
            fields?.affected_ear === AFFECTED_EAR?.BOTH) && (
            <Box
              pb={5}
              sx={{
                backgroundColor: "#F6FAFF",
                width: { xs: "100%", sm: "70%", md: "35%" },
                maxHeight: "63vh",
                border: "1px solid rgb(180, 192, 202)",
                borderRadius: "4px",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "center",
                }}
              >
                <Typography color="primary" variant="h5" sx={{ mt:1 }}>
                  • Left
                </Typography>
              </Box>

              <Box sx={{ p: 1 }}>
              <ChartComponent
                allowed_bc={true}
                onValueChange={(index, value, mode) =>
                  handleValueChange(index, value, "bc", "left")
                }
                data={leftBcData}
                title="BC"
              />
              </Box>
            </Box>
          )}
        </Box>
      </Container>
    </>
  );
};

export default memo(Audiogram);
